import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { FunctionComponent, KeyboardEvent, useEffect, useState } from 'react';

import styles from './InlineEditableTitle.module.scss';

interface InlineEditableTitleProps {
    /** Committed value rendered in display mode. */
    value: string;
    /**
     * Called when the user commits a new value (Enter or blur on a non-empty
     * draft). Empty drafts revert silently — the parent doesn't see them, so
     * the committed value never becomes empty through this control.
     */
    onChange: (next: string) => void;
    /**
     * Error message / state. Truthy values:
     *   - Paint the display text + input border red.
     *   - Auto-open the inline edit on the next render so the user can fix
     *     the value without hunting for the pencil.
     * The component does not display the error string anywhere — surface it
     * elsewhere in your form if needed.
     */
    error?: string | null;
    /** Text shown in display mode when `value` is empty. */
    placeholder?: string;
    maxLength?: number;
    /** Accessibility label used by both the display button and the input. */
    ariaLabel?: string;
    /** Forwarded to both the display button and the input. */
    className?: string;
}

/**
 * Inline-editable bold title — display shows the value with a pencil affordance,
 * click to edit, commit on Enter / blur (empty drafts revert), cancel on Escape.
 *
 * Use it as a prose-embedded title editor (page headers, item titles, screen
 * heroes). For form fields where the input chrome should always be visible,
 * use `V3/Forms/Input` instead — this primitive is intentionally minimal in
 * display mode so it disappears into the surrounding header / hero.
 */
export const InlineEditableTitle: FunctionComponent<InlineEditableTitleProps> = ({
    value,
    onChange,
    error,
    placeholder = 'Untitled',
    maxLength,
    ariaLabel = 'Editable title',
    className
}) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [draft, setDraft] = useState<string>(value);

    // External error → open the inline edit so the user can fix on the spot.
    // The `value` is re-seeded into `draft` so they don't lose their committed
    // text if they re-enter without clicking pencil first.
    useEffect(() => {
        if (error) {
            setDraft(value);
            setIsEditing(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    const startEditing = () => {
        setDraft(value);
        setIsEditing(true);
    };

    const commitEdit = () => {
        const trimmed = draft.trim();
        if (trimmed && trimmed !== value) {
            onChange(trimmed);
        }
        // Empty / unchanged draft → revert silently (parent doesn't see it).
        setIsEditing(false);
    };

    const cancelEdit = () => {
        setIsEditing(false);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commitEdit();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            cancelEdit();
        }
    };

    if (isEditing) {
        return (
            <input
                type="text"
                autoFocus
                value={draft}
                onChange={event => setDraft(event.target.value)}
                onBlur={commitEdit}
                onKeyDown={handleKeyDown}
                maxLength={maxLength}
                aria-label={ariaLabel}
                placeholder={placeholder}
                className={clsx(styles.input, { [styles.inputError]: Boolean(error) }, className)}
            />
        );
    }

    return (
        <button
            type="button"
            onClick={startEditing}
            aria-label={ariaLabel}
            className={clsx(styles.button, className)}
        >
            <span
                className={clsx(styles.text, {
                    [styles.textPlaceholder]: !value,
                    [styles.textError]: Boolean(error)
                })}
            >
                {value || placeholder}
            </span>
            <FontAwesomeIcon
                icon={faPenToSquare}
                className={styles.icon}
            />
        </button>
    );
};

export default InlineEditableTitle;
